import {
  HttpErrorResponse,
  HttpResponse
}                           from '@angular/common/http';
import { Injectable }       from '@angular/core';
import { NgbModal }         from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';

import { ApiService }                        from '../api/api.service';
import { ConfirmService }                    from '../messages/confirm/confirm.service';
import { DvlaLookupMotHistoryComponent }     from './dvla-lookup.mot-history.component';
import { DvlaLookupMotHistoryResponseModel } from './models/dvla.mot-history.response';
import { DvlaLookupPriceModel }              from './models/dvla-lookup.price';
import { MessagesService }                   from '../messages/messages.service';
import { PurchaseDvlaLookupsComponent }      from './dvla-lookup.purchase.component';
import { SessionModel }                      from '../sessions/models/session';
import { SessionService }                    from '../sessions/session.service';
import { STATUSCODES }                       from '../api/api.status-codes.constant';
import { VehicleModel }                      from '../vehicles/models/vehicle';

@Injectable({
  providedIn: 'root'
})
export class DvlaLookupService {
  private _invalidNumberMessage = 'DVLALookup Error Code KeyInvalid - KeyInvalid: The VRM key value is not recognised as a valid vehicle registration mark.';
  private _STATUSCODES          = STATUSCODES;

  constructor (
    private apiService     : ApiService,
    private confirmService : ConfirmService,
    private messagesService: MessagesService,
    private modalService   : NgbModal,
    private sessionService : SessionService,
    public translateService: TranslateService
  ) {}

  activate () : ng.IPromise<SessionModel> {
    return this.apiService.get('/company/add_dvla_lookup')
    .then(() => this.sessionService.refresh());
  }

  deactivate () : ng.IPromise<SessionModel> {
    return this.apiService.get('/company/remove_dvla_lookup')
    .then(() => this.sessionService.refresh());
  }

  hasInsufficientRequests ( err : HttpErrorResponse ) : boolean {
    return err.status === this._STATUSCODES.paymentRequired
      && err.error
      && err.error.type === 'Q';
  }

  isActive ( stats : any ) {
    return stats?.dvla_lookup_active;
  }

  isRegistrationNumberInvalid ( err : HttpErrorResponse ) : boolean {
    return err.error
      && err.error.type === 'R'
      && err.error.message === this._invalidNumberMessage;
  }

  purchase () : ng.IPromise<boolean> {
    return this.apiService.get('/chargify/service_cost', 'dvla_lookup')
      .then(( price : DvlaLookupPriceModel ) => {
        const modal = this.modalService.open(PurchaseDvlaLookupsComponent);

        modal.componentInstance.price = price;

        return modal.result;
      });
  }

  search ( vehicle : VehicleModel ) : ng.IPromise<any> {
    if (!vehicle.plate_number && !vehicle.vin) {
      return Promise.reject(this.translateService.instant('JS_SPACE.CHECKA.PROVIDE_REGO_OR_VIN_NUMBER'));
    }

    return this.apiService.post('/vehicles/find_on_dvla_lookup', vehicle, null, null, { verbose: true })
      .then(( response : HttpResponse<VehicleModel> ) => response.body)
      .catch(( err : HttpErrorResponse ) => {
        if (this.hasInsufficientRequests(err)) {
          this.messagesService.error(this.translateService.instant('JS_SPACE.CHECKA.INSUFFICIENT_REQUESTS'));

          setTimeout(() => {
            this.purchase()
              .then(() => this.search(vehicle));
          }, 0);
        }

        if ( this.isRegistrationNumberInvalid(err) ) {
          this.messagesService.error(this.translateService.instant('JS_SPACE.CHECKA.INVALID_REGISTRATION_NUMBER'));
        }

        return Promise.reject(err.error ? err.error : err);
      });
  }

  updateMotHistory ( vehicle : VehicleModel ) : ng.IPromise<DvlaLookupMotHistoryResponseModel> {
    return this.apiService.get('/vehicles/update_vehicle_mot_history', vehicle.id, null, { verbose: true })
      .catch(( err : HttpErrorResponse ) => {
        if ( this.hasInsufficientRequests(err) ) {
          this.messagesService.error(this.translateService.instant('JS_SPACE.CHECKA.INSUFFICIENT_REQUESTS'));

          return this.purchase()
            .then(() => this.updateMotHistory(vehicle));
        }

        if ( this.isRegistrationNumberInvalid(err) ) {
          this.messagesService.error(this.translateService.instant('JS_SPACE.CHECKA.INVALID_REGISTRATION_NUMBER'));
        }

        return Promise.reject(err.error ? err.error : err);
      });
  }

  viewMotHistory ( vehicle : VehicleModel ) : void {
    this.apiService.get('/vehicles/get_vehicle_mot_history', vehicle.id)
    .then(( history : DvlaLookupMotHistoryResponseModel ) => {
      return history.message
        ? this.confirmService.generic(history.message, undefined, {
          cancelText : this.translateService.instant('GENERAL_SPACE.BUTTON.CANCEL'),
          confirmText: this.translateService.instant('GENERAL_SPACE.BUTTON.UPDATE')
        })
        .then(() => this.updateMotHistory(vehicle))
        : Promise.resolve(history)
    })
    .then(( history : DvlaLookupMotHistoryResponseModel ) => {
      const modal = this.modalService.open(DvlaLookupMotHistoryComponent,{
        windowClass: 'x-lg-modal'
      });

      modal.componentInstance.history = history;
      modal.componentInstance.vehicle = vehicle;

      modal.result;
    });
  }
}